import { useAdminContent } from "@/context/AdminContentContext";
import { Field, Panel } from "@/components/admin/Field";
import { Switch } from "@/components/ui/switch";
import { DEFAULT_THEME, FONTS, THEME_PRESETS, isHex } from "@/lib/theme";

const EFFECTS = [
  { key: "cursor", label: "Cursor personalizado", hint: "Seta laranja com rasto neon e efeito magnético nos botões." },
  { key: "particles", label: "Partículas no topo", hint: "Pontos laranja/turquesa que fogem do rato na página inicial." },
  { key: "sounds", label: "Sons de interface", hint: "Botão de som no canto inferior direito (desligado por defeito para o visitante)." },
];

const CURSOR_COLORS = [
  { id: "orange", name: "Laranja", value: null },
  { id: "turquoise", name: "Turquesa", value: "#14B8A6" },
];

function ColorField({ id, label, hint, value, onChange }) {
  const valid = isHex(value);
  return (
    <Field label={label} id={id} hint={hint}>
      <div className="flex items-center gap-3">
        <input type="color" id={`${id}-picker`} value={valid ? value : "#000000"} onChange={(e) => onChange(e.target.value.toUpperCase())} className="h-10 w-14 cursor-pointer rounded-sm border border-eden-border bg-eden-bg p-1" aria-label={`${label} (seletor)`} data-testid={`${id}-picker`} />
        <input id={id} value={value} onChange={(e) => onChange(e.target.value.trim())} maxLength={7} placeholder="#FF5500" className={`input-eden max-w-[140px] font-mono uppercase ${valid ? "" : "border-red-500"}`} data-testid={`input-${id}`} />
        <span className="h-10 flex-1 rounded-sm border border-eden-border" style={{ background: valid ? value : "transparent" }} aria-hidden="true" />
      </div>
      {!valid && <p className="text-xs text-red-400">Usa um código hexadecimal com 6 dígitos, ex.: #FF5500.</p>}
    </Field>
  );
}

export default function EditTema() {
  const { draft, update } = useAdminContent();
  const theme = { ...DEFAULT_THEME, ...(draft.theme || {}) };
  const set = (k) => (v) => update("theme", { [k]: v });
  return (
    <>
      <Panel title="Cores do site" description="A cor principal (botões, destaques, títulos de secção) e a cor secundária (ícones e detalhes). A pré-visualização aplica-se logo aqui no painel; Guardar publica no site.">
        <div className="grid gap-6 md:grid-cols-2">
          <ColorField id="theme-orange" label="Cor principal" hint="Por omissão: laranja EDEN #FF5500." value={theme.orange} onChange={set("orange")} />
          <ColorField id="theme-turquoise" label="Cor secundária" hint="Por omissão: turquesa #14B8A6." value={theme.turquoise} onChange={set("turquoise")} />
        </div>
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-eden-border bg-eden-bg p-4" data-testid="theme-preview">
          <span className="eyebrow">Pré-visualização</span>
          <button type="button" className="btn-primary h-10">Jogar agora</button>
          <span className="rounded-full border border-eden-border px-3 py-1 text-xs text-eden-secondary"><span className="mr-2 inline-block h-2 w-2 rounded-full bg-eden-turquoise" />Detalhe secundário</span>
          <span className="font-mono text-sm text-eden-orange">zjj9jqp</span>
        </div>
      </Panel>
      <Panel title="Efeitos interativos" description="Extras visuais do site público. Desliga os que não quiseres; os efeitos já se desativam sozinhos em telemóveis e para quem tem “reduzir movimento” ativo.">
        <div className="divide-y divide-eden-border rounded-md border border-eden-border bg-eden-bg" data-testid="effects-editor">
          {EFFECTS.map((e) => {
            const on = theme[e.key] !== false;
            return (
              <div key={e.key} className="flex items-center justify-between gap-4 p-4" data-testid={`effect-row-${e.key}`}>
                <div>
                  <p className="text-sm font-medium text-eden-text">{e.label}</p>
                  <p className="text-xs text-eden-muted">{on ? e.hint : "Desligado no site público."}</p>
                </div>
                <Switch checked={on} onCheckedChange={(v) => update("theme", { [e.key]: v })} aria-label={e.label} data-testid={`effect-switch-${e.key}`} />
              </div>
            );
          })}
        </div>
        {theme.cursor !== false && (
          <div className="rounded-md border border-eden-border bg-eden-bg p-4" data-testid="cursor-color-editor">
            <p className="text-sm font-medium text-eden-text">Cor da seta e do rasto</p>
            <div className="mt-3 flex flex-wrap items-center gap-2" role="radiogroup" aria-label="Cor do cursor">
              {CURSOR_COLORS.map((c) => {
                const value = c.value || theme.orange;
                const active = (theme.cursor_color || theme.orange).toUpperCase() === value.toUpperCase();
                return (
                  <button key={c.name} type="button" role="radio" aria-checked={active} onClick={() => update("theme", { cursor_color: value })} className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors ${active ? "border-eden-orange bg-eden-surface text-eden-text" : "border-eden-border text-eden-secondary hover:border-eden-secondary"}`} data-testid={`cursor-color-${c.id}`}>
                    <span className="h-3.5 w-3.5 rounded-full" style={{ background: value, boxShadow: `0 0 8px ${value}` }} aria-hidden="true" /> {c.name}
                  </button>
                );
              })}
              <label className="inline-flex items-center gap-2 rounded-full border border-eden-border px-3 py-1.5 text-sm text-eden-secondary hover:border-eden-secondary" htmlFor="cursor-color-custom">
                <input id="cursor-color-custom" type="color" value={isHex(theme.cursor_color) ? theme.cursor_color : theme.orange} onChange={(e) => update("theme", { cursor_color: e.target.value.toUpperCase() })} className="h-4 w-4 cursor-pointer rounded-full border-0 bg-transparent p-0" aria-label="Cor personalizada" data-testid="cursor-color-custom" />
                Personalizada
              </label>
            </div>
          </div>
        )}
      </Panel>
      <Panel title="Tipo de letra dos títulos" description="Aplica-se a títulos, botões e destaques. O texto corrido mantém-se em DM Sans para boa leitura.">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" role="radiogroup" aria-label="Tipo de letra dos títulos">
          {FONTS.map((f) => {
            const active = (theme.font || "outfit") === f.id;
            return (
              <button key={f.id} type="button" role="radio" aria-checked={active} onClick={() => update("theme", { font: f.id })} className={`rounded-md border p-4 text-left transition-colors ${active ? "border-eden-orange bg-eden-surface" : "border-eden-border hover:border-eden-secondary"}`} data-testid={`theme-font-${f.id}`}>
                <p className="text-2xl font-bold leading-none text-eden-text" style={{ fontFamily: f.family }}>A tua nova realidade</p>
                <p className="mt-3 text-sm font-medium text-eden-text">{f.name}</p>
                <p className="text-xs text-eden-muted">{f.note}</p>
              </button>
            );
          })}
        </div>
      </Panel>
      <Panel title="Combinações prontas" description="Clica para aplicar. Podes afinar depois nos campos acima.">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {THEME_PRESETS.map((p) => {
            const active = p.orange === theme.orange && p.turquoise === theme.turquoise;
            return (
              <button key={p.name} type="button" onClick={() => update("theme", { orange: p.orange, turquoise: p.turquoise })} className={`flex items-center gap-3 rounded-md border p-3 text-left text-sm transition-colors ${active ? "border-eden-orange bg-eden-surface" : "border-eden-border hover:border-eden-secondary"}`} data-testid={`theme-preset-${p.name.replace(/\s+/g, "-").toLowerCase()}`}>
                <span className="flex gap-1"><span className="h-6 w-6 rounded-sm" style={{ background: p.orange }} /><span className="h-6 w-6 rounded-sm" style={{ background: p.turquoise }} /></span>
                <span className="text-eden-text">{p.name}</span>
              </button>
            );
          })}
        </div>
      </Panel>
    </>
  );
}
